import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {motion} from 'framer-motion';
import './DecryptedText.css';

const fillIndices=(length)=> new Set(
  Array.from({length},(_,index)=> index)
);

function getRevealOrder(length,direction) {
  if (direction==='end') {
    return Array.from({length},(_,index)=> length - index - 1);
  }

  if (direction==='center') {
    const order=[];
    const middle=Math.floor((length - 1) / 2);

    for (let offset=0;order.length < length;offset +=1) {
      const right=middle + offset;
      const left=middle - offset;

      if (right < length) order.push(right);
      if (offset > 0 && left >=0) order.push(left);
    }

    return order;
  }

  return Array.from({length},(_,index)=> index);
}

export default function DecryptedText({
  text='',
  speed=50,
  maxIterations=10,
  sequential=false,
  revealDirection='start',
  useOriginalCharsOnly=false,
  characters='ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*()_+',
  className='',
  parentClassName='',
  encryptedClassName='',
  animateOn='hover',
  clickMode='once',
  ...props
}) {
  const [displayText,setDisplayText]=useState(text);
  const [isAnimating,setIsAnimating]=useState(false);
  const [isDecrypted,setIsDecrypted]=useState(animateOn!=='click');
  const [revealed,setRevealed]=useState(()=> fillIndices(text.length));
  const [hasAnimated,setHasAnimated]=useState(false);
  const containerRef=useRef(null);
  const timerRef=useRef(null);

  const availableChars=useMemo(()=> {
    const source=useOriginalCharsOnly
      ? [...new Set(text.replace(/\s/g,'').split(''))]
      : characters.split('');

    return source.length ? source : ['•'];
  },[characters,text,useOriginalCharsOnly]);

  const scramble=useCallback((visibleIndices)=> (
    text.split('').map((character,index)=> {
      if (/\s/.test(character) || visibleIndices.has(index)) {
        return character;
      }

      return availableChars[
        Math.floor(Math.random() * availableChars.length)
      ];
    }).join('')
  ),[availableChars,text]);

  const stopAnimation=useCallback(()=> {
    window.clearInterval(timerRef.current);
    timerRef.current=null;
    setIsAnimating(false);
  },[]);

  const revealText=useCallback(()=> {
    stopAnimation();
    const reducedMotion=window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reducedMotion) {
      setDisplayText(text);
      setRevealed(fillIndices(text.length));
      setIsDecrypted(true);
      return;
    }

    const order=getRevealOrder(text.length,revealDirection)
      .filter((index)=> !/\s/.test(text[index]));
    const visible=new Set();
    let pointer=0;
    let iteration=0;

    setRevealed(new Set());
    setDisplayText(scramble(visible));
    setIsDecrypted(false);
    setIsAnimating(true);

    timerRef.current=window.setInterval(()=> {
      iteration +=1;

      if (sequential) {
        const perTick=Math.max(1,Math.ceil(order.length / maxIterations));
        for (let count=0;count < perTick;count +=1) {
          if (pointer < order.length) {
            visible.add(order[pointer]);
            pointer +=1;
          }
        }
      }

      setRevealed(new Set(visible));
      setDisplayText(scramble(visible));

      const finished=sequential
        ? pointer >=order.length
        : iteration >=maxIterations;

      if (finished) {
        stopAnimation();
        setDisplayText(text);
        setRevealed(fillIndices(text.length));
        setIsDecrypted(true);
      }
    },speed);
  },[
    maxIterations,
    revealDirection,
    scramble,
    sequential,
    speed,
    stopAnimation,
    text
  ]);

  const encryptText=useCallback(()=> {
    stopAnimation();
    const empty=new Set();
    setRevealed(empty);
    setDisplayText(scramble(empty));
    setIsDecrypted(false);
  },[scramble,stopAnimation]);

  useEffect(()=> {
    setDisplayText(text);
    setRevealed(fillIndices(text.length));
    setIsDecrypted(animateOn!=='click');
  },[animateOn,text]);

  useEffect(()=> {
    if (animateOn!=='view' && animateOn!=='inViewHover') return undefined;

    const element=containerRef.current;
    if (!element) return undefined;

    const observer=new IntersectionObserver((entries)=> {
      if (entries.some((entry)=> entry.isIntersecting) && !hasAnimated) {
        revealText();
        setHasAnimated(true);
      }
    },{threshold: 0.35});

    observer.observe(element);
    return ()=> observer.disconnect();
  },[animateOn,hasAnimated,revealText]);

  useEffect(()=> ()=> stopAnimation(),[stopAnimation]);

  const interactionProps={};

  if (animateOn==='hover' || animateOn==='inViewHover') {
    interactionProps.onMouseEnter=revealText;
  }

  if (animateOn==='click') {
    interactionProps.onClick=()=> {
      if (clickMode==='toggle' && isDecrypted) {
        encryptText();
      } else if (!isDecrypted || clickMode==='once') {
        revealText();
      }
    };
    interactionProps.role='button';
    interactionProps.tabIndex=0;
  }

  return (
    <motion.span
      ref={containerRef}
      className={`decrypted-text ${parentClassName}`}
      {...interactionProps}
      {...props}
    >
      <span className="decrypted-text__sr">{text}</span>
      <span aria-hidden="true">
        {displayText.split('').map((character,index)=> {
          const visible=revealed.has(index) || (!isAnimating && isDecrypted);

          return (
            <span
              key={`${index}-${character}`}
              className={visible ? className : encryptedClassName}
            >
              {character}
            </span>
          );
        })}
      </span>
    </motion.span>
  );
}